// ============================================================
// TERROR ENGINE — PLAYER ENTITY
// Universal player + companion rendering.
// Used by Plaza, Workshop, FactionHall, Dungeon, and any future zone.
//
// Props:
//   pos        { x, y }         — tile position
//   facing     { x, y }         — direction (-1/0/1)
//   gender     "f" | "m"
//   walkFrame  boolean           — alternates for walk animation
//   isIdle     boolean
//   idleFrame  boolean           — female idle _b frame
//   tile       number            — tile size in px
//   opacity    number            — for dungeon fade effects
//   companionId string | null    — "default" | "jynxi" | null
//   showCompanion boolean
//   size       number            — multiplier (default 1.5 × tile)
//   equipped   { head, face, body, back, aura, title } | null
//                                 — cosmetic overlay; null = no cosmetics rendered
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { getPlayerSprite, getIdleSprite, getCompanionSprite } from "./SpriteManifest";
import { CosmeticOverlay } from "./CosmeticOverlay";

export function PlayerEntity({
  pos,
  facing     = { x: 0, y: 1 },
  gender     = "f",
  walkFrame  = false,
  isIdle     = false,
  idleFrame  = false,
  tile       = 48,
  opacity    = 1,
  companionId = null,
  showCompanion = true,
  equipped   = null,
}) {
  const size = Math.floor(tile * 1.5);

  // ── Player sprite resolution ──────────────────────────────
  const src = isIdle
    ? getIdleSprite(gender, idleFrame)
    : getPlayerSprite(gender, facing.x, facing.y, walkFrame);

  const [imgErr, setImgErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) { prevSrc.current = src; setImgErr(false); }
  }, [src]);

  // ── Companion position — trails behind player ─────────────
  const companionSrc = companionId ? getCompanionSprite(companionId) : null;
  const [compErr, setCompErr]   = useState(false);
  const companionSize = Math.floor(tile * 1.3);
  const fx = facing.x || 0;
  const fy = facing.y || 1;
  const gap = tile * 1.5;
  const compLeft = pos.x * tile - fx * gap * 2 - companionSize / 2 + tile / 2;
  const compTop  = pos.y * tile + (fy === -1 ? gap * 2 : -fy * gap) - companionSize + tile;

  return (
    <>
      {/* Companion */}
      {showCompanion && companionSrc && !compErr && (
        <div style={{
          position: "absolute",
          left: compLeft, top: compTop,
          width: companionSize, height: companionSize,
          zIndex: 9,
          transition: "left 0.12s, top 0.12s",
          pointerEvents: "none",
          filter: "drop-shadow(0 0 6px #c9a84c44)",
        }}>
          <img
            key={companionSrc}
            src={companionSrc}
            alt="companion"
            onError={() => setCompErr(true)}
            style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
          />
        </div>
      )}

      {/* Player */}
      <div style={{
        position: "absolute",
        left: pos.x * tile - size / 2 + tile / 2,
        top:  pos.y * tile - size + tile,
        width: size, height: size,
        zIndex: 10,
        transition: "left 0.08s, top 0.08s, opacity 0.3s ease",
        opacity,
        pointerEvents: "none",
      }}>
        {src && !imgErr
          ? <img
              key={src}
              src={src}
              alt="player"
              onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
            />
          : <div style={{
              width: "100%", height: "100%",
              background: "#c9a84c",
              borderRadius: 4,
              border: "2px solid #d4a84b",
            }} />
        }

        {/* Cosmetic overlay — no-op when equipped is null */}
        <CosmeticOverlay equipped={equipped} size={size} gender={gender} facing={facing} />
      </div>
    </>
  );
}

// ── Walk/idle animation hook ──────────────────────────────────
// Drop into any zone's main component to get walk + idle state.
// Returns { walkFrame, isIdle, idleFrame } to pass into PlayerEntity.
export function usePlayerAnimation(pos) {
  const [walkFrame, setWalkFrame]   = useState(false);
  const [isIdle, setIsIdle]         = useState(false);
  const [idleFrame, setIdleFrame]   = useState(false);
  const walkFrameRef  = useRef(false);
  const lastPosRef    = useRef({ x: pos.x, y: pos.y });
  const idleTimerRef  = useRef(null);
  const idleAnimRef   = useRef(null);

  useEffect(() => {
    if (pos.x !== lastPosRef.current.x || pos.y !== lastPosRef.current.y) {
      lastPosRef.current = { x: pos.x, y: pos.y };
      walkFrameRef.current = !walkFrameRef.current;
      setWalkFrame(walkFrameRef.current);
      setIsIdle(false);
      setIdleFrame(false);
      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        idleAnimRef.current = setInterval(() => setIdleFrame(f => !f), 1800);
      }, 3000);
    }
    return () => {      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
    };
  }, [pos.x, pos.y]);

  return { walkFrame, isIdle, idleFrame };
}

// ── Mobile D-Pad ─────────────────────────────────────────────
// Identical across all zones. One definition, imported everywhere.
// ⭐ ONE isMobile, EXPORTED. It was written out by hand in PierHome.js
// and UnstablePierre.js, and the Minerium — which needs it most, being
// the only walkable screen with no thumb controls at all — did not have
// it. A third hand-written copy is how three screens start disagreeing
// about what a phone is. It lives here because this is the file the
// D-pad lives in, and nothing ever needs one without the other.
//
// `(pointer: coarse)` asks about the PRIMARY pointer rather than what
// the hardware merely supports: phone and tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
export const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};

export function MobileDpad({ keysRef }) {
  const dirs = [
    { key: "ArrowUp",    label: "▲", col: 2, row: 1 },
    { key: "ArrowLeft",  label: "◀", col: 1, row: 2 },
    { key: "ArrowDown",  label: "▼", col: 2, row: 3 },
    { key: "ArrowRight", label: "▶", col: 3, row: 2 },
  ];
  const press    = (key) => { keysRef.current[key] = true; };
  const release  = (key) => { keysRef.current[key] = false; };
  const releaseAll = () => {
    ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].forEach(k => { keysRef.current[k] = false; });
  };
  const btn = (col, row) => ({
    gridColumn: col, gridRow: row,
    width: 48, height: 48,
    background: "rgba(10,5,30,0.85)",
    border: "1px solid rgba(161,130,250,0.4)",
    borderRadius: 10, color: "rgba(200,180,255,0.9)", fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", userSelect: "none", WebkitUserSelect: "none",
    touchAction: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.7)",
  });
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 20, zIndex: 200,
      display: "grid",
      gridTemplateColumns: "48px 48px 48px",
      gridTemplateRows: "48px 48px 48px",
      gap: 5, touchAction: "none",
    }}>
      {dirs.map(({ key, label, col, row }) => (
        <div key={key} style={btn(col, row)}
          onPointerDown={e => { e.preventDefault(); press(key); }}
          onPointerUp={e => { e.preventDefault(); release(key); }}
          onPointerLeave={e => { e.preventDefault(); release(key); }}
          onPointerCancel={e => { e.preventDefault(); releaseAll(); }}>
          {label}
        </div>
      ))}
    </div>
  );
}
